import { Link } from "react-router";
import { ArrowRight, Award, Check, Target } from "lucide-react";
import { cn } from "@/lib/utils";
import type {
  PortalContentResolution,
  Tier,
  TierProgramRow,
  TierProgramMatrixRow,
  TierProgramCell,
} from "@colab/shared";

type LevelUpWidgetProps = {
  program: TierProgramRow | null;
  tiers: Tier[];
  rows: TierProgramMatrixRow[];
  cells: TierProgramCell[];
  currentTierId: string | null;
  resolution: PortalContentResolution<"level_up">;
};

// Partner-facing tier-ladder ("Level up") widget. Hides itself entirely if
// no default tier program exists (pre-cutover). If the partner has no
// assignment yet, renders a "not assigned" empty state.
export function LevelUpWidget({
  program,
  tiers,
  rows,
  cells,
  currentTierId,
  resolution,
}: LevelUpWidgetProps) {
  if (!program || tiers.length === 0) return null;

  const content = resolution.resolved?.content ?? null;
  const heading = content?.heading ?? "Level up your partnership";
  const subheading = content?.subheading ?? null;
  const ctaLabel = content?.cta_label ?? null;
  const ctaHref = content?.cta_href ?? null;

  const currentIndex = tiers.findIndex((t) => t.id === currentTierId);
  const nextTier =
    currentIndex >= 0 && currentIndex < tiers.length - 1
      ? tiers[currentIndex + 1]
      : null;

  // Requirements/benefits for the next tier, pulled from the matrix cells that
  // already exist admin-side. Only rows with a non-empty cell value for the
  // next tier are worth showing.
  const cellValue = (rowId: string, tierId: string): string | null => {
    const cell = cells.find((c) => c.row_id === rowId && c.tier_id === tierId);
    const v = cell?.value?.trim();
    return v && v.length > 0 ? v : null;
  };
  const nextTierRows = nextTier
    ? rows
        .map((r) => ({ row: r, value: cellValue(r.id, nextTier.id) }))
        .filter((x): x is { row: TierProgramMatrixRow; value: string } =>
          Boolean(x.value),
        )
    : [];
  const nextRequirements = nextTierRows.filter(
    (x) => x.row.row_kind === "requirement",
  );
  const nextBenefits = nextTierRows.filter((x) => x.row.row_kind === "benefit");

  return (
    <section className="rounded-xl border bg-card p-6">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <div className="flex items-center gap-2">
            <Award className="h-4 w-4 text-accent" />
            <h2 className="font-display text-sm font-semibold uppercase tracking-wider text-muted-foreground">
              {heading}
            </h2>
          </div>
          {subheading ? (
            <p className="mt-1 max-w-xl text-xs text-muted-foreground">{subheading}</p>
          ) : null}
        </div>
        {ctaLabel && ctaHref ? (
          <Link
            to={ctaHref}
            className="inline-flex items-center gap-1 text-xs font-medium text-accent hover:underline"
          >
            {ctaLabel}
            <ArrowRight className="h-3 w-3" />
          </Link>
        ) : null}
      </div>

      {currentTierId === null ? (
        <p className="mt-4 rounded-md border border-dashed bg-muted/30 px-4 py-3 text-sm text-muted-foreground">
          You haven&apos;t been assigned to a tier in the {program.name} program yet — reach out to
          the partnerships team once you&apos;ve closed your first qualifying deal.
        </p>
      ) : (
        <div className="mt-4 grid gap-2 sm:grid-cols-2 lg:grid-cols-4">
          {tiers.map((tier, idx) => {
            const isCurrent = tier.id === currentTierId;
            const isPast = currentIndex >= 0 && idx < currentIndex;
            return (
              <div
                key={tier.id}
                className={cn(
                  "rounded-lg border p-3 transition",
                  isCurrent
                    ? "border-accent bg-accent/5 ring-1 ring-accent"
                    : isPast
                      ? "border-foreground/15 bg-muted/20"
                      : "border-border bg-card opacity-70",
                )}
                style={
                  isCurrent && tier.color_hex
                    ? { borderColor: tier.color_hex, boxShadow: `0 0 0 1px ${tier.color_hex}` }
                    : undefined
                }
              >
                <div className="flex items-center justify-between">
                  <span className="text-[10px] font-medium uppercase tracking-wider text-muted-foreground">
                    Tier {idx + 1}
                  </span>
                  {isCurrent ? (
                    <span className="rounded-full bg-accent px-2 py-0.5 text-[10px] font-medium text-accent-foreground">
                      You
                    </span>
                  ) : null}
                </div>
                <p
                  className={cn(
                    "mt-1 font-display text-base font-semibold tracking-tight",
                    isCurrent ? "text-foreground" : "text-foreground/80",
                  )}
                >
                  {tier.name}
                </p>
              </div>
            );
          })}
        </div>
      )}

      {nextTier && (nextRequirements.length > 0 || nextBenefits.length > 0) ? (
        <div className="mt-4 rounded-lg border bg-muted/20 p-4">
          <p className="text-xs font-medium text-muted-foreground">
            What it takes to reach{" "}
            <span className="font-semibold text-foreground">{nextTier.name}</span>
          </p>
          {nextRequirements.length > 0 ? (
            <ul className="mt-3 space-y-2">
              {nextRequirements.map(({ row, value }) => (
                <li key={row.id} className="flex items-start gap-2 text-sm">
                  <Target className="mt-0.5 h-3.5 w-3.5 shrink-0 text-accent" />
                  <span>
                    <span className="font-medium text-foreground">{row.label}:</span>{" "}
                    <span className="text-muted-foreground">{value}</span>
                  </span>
                </li>
              ))}
            </ul>
          ) : null}
          {nextBenefits.length > 0 ? (
            <>
              <p className="mt-4 text-xs font-medium text-muted-foreground">
                What you&apos;ll unlock
              </p>
              <ul className="mt-2 space-y-2">
                {nextBenefits.map(({ row, value }) => (
                  <li key={row.id} className="flex items-start gap-2 text-sm">
                    <Check className="mt-0.5 h-3.5 w-3.5 shrink-0 text-emerald-600" />
                    <span>
                      <span className="font-medium text-foreground">{row.label}:</span>{" "}
                      <span className="text-muted-foreground">{value}</span>
                    </span>
                  </li>
                ))}
              </ul>
            </>
          ) : null}
        </div>
      ) : currentIndex >= 0 && !nextTier ? (
        <p className="mt-4 text-xs text-muted-foreground">
          You&apos;re at the top tier — nicely done.
        </p>
      ) : null}
    </section>
  );
}
